import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ShoppingCart, X, Plus, Minus, Star, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../lib/images";
import api, { formatApiErrorDetail } from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import FullImage from "../components/site/FullImage";
import Newsletter from "../components/site/Newsletter";
import ProductEditorModal from "../components/site/ProductEditorModal";
import { addToCart, useCart, cartCount } from "../lib/cart";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import CurrencySelector from "../components/site/CurrencySelector";

function ProductCard({ p, featured, isAdmin, onOpen, onEdit, onDelete }) {
  const { format } = useCurrency();
  return (
    <div className={`group relative rounded-2xl overflow-hidden bg-cream transition-all duration-300 hover:-translate-y-1 ${featured ? "ring-2 ring-gold/70 shadow-lg hover:ring-gold" : "border border-parchment hover:border-gold"}`}>
      {featured && (
        <span className="absolute top-3 left-3 z-20 inline-flex items-center gap-1 bg-gold text-night text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow"><Star className="w-3 h-3 fill-night" /> Featured</span>
      )}
      {isAdmin && (
        <div className="absolute top-3 right-3 z-20 flex gap-2">
          <button onClick={(e) => { e.stopPropagation(); onEdit(p); }} data-testid={`shop-edit-product-${p.id}`} title="Edit" className="grid place-items-center w-9 h-9 rounded-full bg-night/80 text-cream hover:bg-forest backdrop-blur transition-colors"><Pencil className="w-4 h-4" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(p); }} data-testid={`shop-delete-product-${p.id}`} title="Remove" className="grid place-items-center w-9 h-9 rounded-full bg-night/80 text-cream hover:bg-red-600 backdrop-blur transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
      <button onClick={() => onOpen(p)} data-testid={`${featured ? "featured" : "product"}-card-${p.id}`} className="text-left w-full block">
        {(p.images || [])[0] ? <FullImage src={p.images[0]} alt={p.name} className="aspect-square" imgClassName="group-hover:scale-105 transition-transform duration-700" /> : <div className="aspect-square bg-forest-deep" />}
        <div className="p-5">
          <span className="text-[10px] uppercase tracking-widest text-gold">{p.category}</span>
          <h3 className="font-serif text-xl text-ink mt-1">{p.name}</h3>
          <p className="font-serif text-lg text-forest mt-1">{format(p.price)}</p>
          {isAdmin && !p.published && <span className="inline-block mt-2 text-xs bg-ink/10 text-ink-muted rounded px-2 py-0.5">Hidden</span>}
        </div>
      </button>
    </div>
  );
}


function ProductModal({ p, onClose }) {
  const { format, isConverted, ratesUpdatedText } = useCurrency();
  const [variant, setVariant] = React.useState({});
  const [qty, setQty] = React.useState(1);
  const [added, setAdded] = React.useState(false);
  const missing = (p.variants || []).some((v) => !variant[v.name]);
  const add = () => {
    if (missing) return;
    addToCart({ id: p.id, name: p.name, price: p.price, image: (p.images || [])[0] || "", variant, qty });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="absolute inset-0 bg-night/70" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-cream rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 z-10 grid place-items-center w-10 h-10 rounded-full bg-night/60 text-cream"><X className="w-5 h-5" /></button>
        {(p.images || [])[0] && <FullImage src={p.images[0]} alt={p.name} className="h-72" />}
        <div className="p-6">
          <span className="text-xs uppercase tracking-widest text-gold">{p.category}</span>
          <h2 className="font-serif text-3xl text-ink mt-1">{p.name}</h2>
          <p className="font-serif text-2xl text-forest mt-2">{format(p.price)}</p>
          {isConverted && <p className="text-xs text-ink-muted mt-0.5">Charged as ${p.price.toFixed(2)} USD at checkout{ratesUpdatedText ? ` · ${ratesUpdatedText.toLowerCase()}` : ""}</p>}
          {p.description && <p className="text-ink-muted mt-3 leading-relaxed whitespace-pre-line">{p.description}</p>}
          {(p.variants || []).map((v) => (
            <div key={v.name} className="mt-4">
              <p className="text-xs uppercase tracking-widest text-ink-muted mb-2">{v.name}</p>
              <div className="flex flex-wrap gap-2">
                {v.options.map((o) => (
                  <button key={o} onClick={() => setVariant((s) => ({ ...s, [v.name]: o }))}
                    data-testid={`variant-${v.name}-${o}`}
                    className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${variant[v.name] === o ? "bg-forest text-cream border-forest" : "border-parchment text-ink hover:border-gold"}`}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-parchment rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid place-items-center w-10 h-10"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-semibold" data-testid="modal-qty">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="grid place-items-center w-10 h-10"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={add} disabled={missing} data-testid="add-to-cart-button"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-forest text-cream font-semibold py-3 rounded-full hover:bg-forest-light transition-colors disabled:opacity-50">
              <ShoppingCart className="w-4 h-4" /> {added ? "Added!" : missing ? "Select options" : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Shop() {
  useSEO("Shop", "Shop authentic Yorùbá cultural products from ÀsÀNigHT Worldwide — textiles, crafts, art and more.");
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [active, setActive] = React.useState(null);
  const [activeCat, setActiveCat] = React.useState("All");
  const [editing, setEditing] = React.useState(null); // null | 'new' | product
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  useCart();

  const load = React.useCallback(() => {
    api.get(isAdmin ? "/admin/products" : "/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdmin]);

  React.useEffect(() => { load(); }, [load]);

  const removeProduct = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the shop?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product removed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed.");
    }
  };

  const autoCats = React.useMemo(() => {
    const set = [];
    products.forEach((p) => { if (p.category && !set.includes(p.category)) set.push(p.category); });
    return set;
  }, [products]);

  const [managedCats, setManagedCats] = React.useState([]);
  React.useEffect(() => {
    api.get("/shop/categories").then((r) => setManagedCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const tabCats = managedCats.length ? managedCats : autoCats;
  const categories = ["All", ...tabCats];
  const showTabs = tabCats.length >= 1 && (managedCats.length > 0 || tabCats.length > 1);

  const visible = activeCat === "All" ? products.filter((p) => !p.featured) : products.filter((p) => p.category === activeCat);
  const featured = React.useMemo(() => products.filter((p) => p.featured), [products]);

  const [banner, setBanner] = React.useState("");
  const [txt, setTxt] = React.useState({ eyebrow: "", title: "", intro: "" });
  React.useEffect(() => {
    api.get("/site-settings").then((r) => {
      const d = r.data || {};
      if (d.banner_shop) setBanner(d.banner_shop);
      setTxt({ eyebrow: d.shop_eyebrow || "", title: d.shop_title || "", intro: d.shop_intro || "" });
    }).catch(() => {});
  }, []);

  return (
    <div>
      {banner ? (
        <header className="relative w-full aspect-[16/9] max-h-[80vh] overflow-hidden bg-night" data-testid="shop-banner-hero">
          <img src={banner} alt={txt.title || "ÀsÀNigHT Market"} className="absolute inset-0 w-full h-full object-contain" />
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <CurrencySelector variant="dark" />
            {isAdmin && (
              <button onClick={() => setEditing("new")} data-testid="shop-add-product" className="inline-flex items-center gap-2 bg-gold text-night text-sm font-bold px-4 py-2 rounded-full hover:bg-gold-light transition-colors shadow">
                <Plus className="w-4 h-4" /> Add product
              </button>
            )}
            <Link to="/shop/cart" data-testid="cart-link" className="relative inline-flex items-center gap-2 bg-gold text-night font-semibold px-5 py-3 rounded-full hover:bg-gold-light transition-colors shadow">
              <ShoppingCart className="w-4 h-4" /> Cart
              <span data-testid="cart-count" className="grid place-items-center min-w-5 h-5 px-1 rounded-full bg-night text-cream text-xs">{cartCount()}</span>
            </Link>
          </div>
        </header>
      ) : (
      <header className="relative min-h-[46vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.indigoCloth || IMAGES.drummers} alt="Shop" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/80 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-14 w-full flex items-end justify-between">
          <div>
            <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-4 inline-flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> {txt.eyebrow || "ÀsÀ Market"}</p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero">{txt.title || "Shop"}</motion.h1>
            {txt.intro && <p className="mt-4 text-cream/80 text-lg max-w-xl">{txt.intro}</p>}
            {isAdmin && (
              <button onClick={() => setEditing("new")} data-testid="shop-add-product" className="mt-4 inline-flex items-center gap-2 bg-gold text-night text-sm font-bold px-4 py-2 rounded-full hover:bg-gold-light transition-colors shadow">
                <Plus className="w-4 h-4" /> Add product
              </button>
            )}
          </div>
          <Link to="/shop/cart" data-testid="cart-link" className="relative inline-flex items-center gap-2 bg-gold text-night font-semibold px-5 py-3 rounded-full hover:bg-gold-light transition-colors">
            <ShoppingCart className="w-4 h-4" /> Cart
            <span data-testid="cart-count" className="grid place-items-center min-w-5 h-5 px-1 rounded-full bg-night text-cream text-xs">{cartCount()}</span>
          </Link>
        </div>
        <div className="absolute top-4 right-4 z-10">
          <CurrencySelector variant="dark" />
        </div>
      </header>
      )}

      <div className="max-w-6xl mx-auto px-6 py-16">
        {!loading && featured.length > 0 && activeCat === "All" && (
          <div className="mb-14" data-testid="shop-featured-row">
            <div className="flex items-center gap-3 mb-5">
              <Star className="w-5 h-5 text-gold fill-gold" />
              <h2 className="font-serif text-2xl text-ink">Featured</h2>
              <span className="h-px flex-1 bg-parchment" />
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}>
                  <ProductCard p={p} featured isAdmin={isAdmin} onOpen={setActive} onEdit={setEditing} onDelete={removeProduct} />
                </Reveal>
              ))}
            </div>
          </div>
        )}
        {!loading && showTabs && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="shop-category-tabs">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`shop-category-${c}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors ${activeCat === c ? "bg-forest text-cream border-forest" : "border-parchment text-ink hover:border-gold"}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        {loading ? (
          <p className="text-ink-muted text-center py-16">Loading…</p>
        ) : products.length === 0 ? (
          <div className="text-center border border-dashed border-parchment rounded-2xl py-14 px-6 bg-sand" data-testid="shop-empty">
            <span className="grid place-items-center w-16 h-16 rounded-full bg-gold/15 text-gold mx-auto mb-4"><ShoppingBag className="w-7 h-7" /></span>
            <p className="font-serif text-2xl text-ink mb-2">Our Market Is Being Stocked</p>
            <p className="text-ink-muted">Beautiful cultural products are coming soon. Check back shortly!</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center border border-dashed border-parchment rounded-2xl py-14 px-6 bg-sand" data-testid="shop-category-empty">
            <span className="grid place-items-center w-16 h-16 rounded-full bg-gold/15 text-gold mx-auto mb-4"><ShoppingBag className="w-7 h-7" /></span>
            <p className="font-serif text-2xl text-ink mb-2">Coming soon to {activeCat}</p>
            <p className="text-ink-muted">No products in this category yet — check back shortly!</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard p={p} isAdmin={isAdmin} onOpen={setActive} onEdit={setEditing} onDelete={removeProduct} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {active && <ProductModal p={active} onClose={() => setActive(null)} />}
      {editing && <ProductEditorModal product={editing} onClose={() => setEditing(null)} onSaved={load} />}
      <Newsletter />
    </div>
  );
}
